import { ITEMS, type ItemId } from "../data/items.ts";
import { SPECIES, type SpeciesId } from "../data/species.ts";
import type { ZoneId } from "../data/map.ts";
import { bagAdd, type SaveState } from "./save.ts";
import { BADGES, DAILY_BONUS, missionDone, missionText, newBadges, rewardText, streakReward, type Mission } from "./goals.ts";

export type GoalKind = "catch" | "wildWin" | "trainerWin" | "superHit" | "condition" | "step" | "rest" | "zone"
  | "duelWin" | "raidDamage" | "raidWin" | "trade";

const itemName = (id: ItemId) => ITEMS[id].name;

/**
 * Apply one player event to the lifetime stats, then report what it finished: missions that just
 * became claimable and badges that were just earned (paid immediately). Returns toast lines.
 */
export function applyGoal(state: SaveState, kind: GoalKind, n = 1, meta: { species?: string; shiny?: boolean; zone?: string } = {}): string[] {
  const s = state.stats;
  const before = state.daily ? state.daily.missions.map((m) => missionDone(state, m)) : [];
  switch (kind) {
    case "catch": {
      s.caught += n;
      const sp = meta.species ? SPECIES[meta.species as SpeciesId] : undefined;
      for (const t of sp?.types ?? []) s.caughtType[t] = (s.caughtType[t] ?? 0) + n;
      if (meta.shiny) s.shinies += n;
      break;
    }
    case "wildWin": s.wildWins += n; break;
    case "trainerWin": s.trainerWins += n; break;
    case "superHit": s.superHits += n; break;
    case "condition": s.conditions += n; break;
    case "step": s.steps += n; break;
    case "rest": s.rests += n; break;
    case "zone": if (meta.zone) s.zoneVisits[meta.zone as ZoneId] = (s.zoneVisits[meta.zone as ZoneId] ?? 0) + n; break;
    case "duelWin": s.duelWins += n; break;
    case "raidDamage": s.raidDamage += n; break;
    case "raidWin": s.raidWins += n; break;
    case "trade": s.trades += n; break;
  }
  const out: string[] = [];
  state.daily?.missions.forEach((m, i) => {
    if (!before[i] && !m.claimed && missionDone(state, m)) out.push(`Mission complete: ${missionText(m)}! Claim it in GOALS.`);
  });
  out.push(...checkBadges(state));
  return out;
}

/** Earn any badge whose goal is now met; pays its coins at once. */
export function checkBadges(state: SaveState): string[] {
  const out: string[] = [];
  for (const b of newBadges(state)) {
    state.badges.push(b.id);
    state.money = Math.min(999999, state.money + b.money);
    out.push(`Badge earned: ${b.name}!${b.money ? ` +${b.money} coins.` : ""} Title "${b.title}" unlocked.`);
  }
  return out;
}

function pay(state: SaveState, r: { money: number; item?: ItemId; count?: number }): void {
  state.money = Math.min(999999, state.money + r.money);
  if (r.item && r.count) bagAdd(state, r.item, r.count);
}

/** Claim a finished mission. Returns the reward line, or null when it is not claimable. */
export function claimMission(state: SaveState, index: number): string | null {
  const m: Mission | undefined = state.daily?.missions[index];
  if (!m || m.claimed || !missionDone(state, m)) return null;
  m.claimed = true;
  state.stats.missionsDone += 1;
  pay(state, m);
  return `+${rewardText(m, itemName)}`;
}

/** The three core missions (not the live bonus) are claimed: pay the chest once. */
export function claimBonus(state: SaveState): string | null {
  const d = state.daily;
  if (!d || d.bonusClaimed || !d.missions.filter((m) => !m.live).every((m) => m.claimed)) return null;
  d.bonusClaimed = true;
  pay(state, DAILY_BONUS);
  return `Daily chest: +${rewardText(DAILY_BONUS, itemName)}`;
}

export function claimStreak(state: SaveState): string | null {
  const d = state.daily;
  if (!d || d.streakClaimed) return null;
  d.streakClaimed = true;
  const r = streakReward(d.streak);
  pay(state, r);
  return r.label;
}

/** Anything waiting to be claimed (drives the "!" on GOALS). */
export function goalsPending(state: SaveState): boolean {
  const d = state.daily;
  if (!d) return false;
  return d.missions.some((m) => !m.claimed && missionDone(state, m)) || (!d.bonusClaimed && d.missions.filter((m) => !m.live).every((m) => m.claimed));
}

export { BADGES };
